import * as React from 'react'
import { Trash2 } from 'lucide-react'
import { FormField } from '@/components/templates/entity-form'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { Contact } from '@/lib/contacts/types'
import type { InvoiceForm, InvoiceFormLineItem, InvoiceResponse } from '@/pages/InvoicesPage.types'
import type { TaxRate } from '@/pages/TaxSettingsPage'
import { InvoiceCategorySelect } from '@/pages/InvoiceCategorySelect'

export function ManualInvoiceDialog({
  contacts,
  contactsLoading,
  taxRates,
  taxRatesLoading,
  pending,
  error,
  currentAssociationDate,
  onClose,
  onSubmit,
}: {
  contacts: Contact[]
  contactsLoading: boolean
  taxRates: TaxRate[]
  taxRatesLoading: boolean
  pending: boolean
  error: Error | null
  currentAssociationDate?: string
  onClose: () => void
  onSubmit: (form: InvoiceForm) => void
}) {
  const [form, setForm] = React.useState<InvoiceForm>({
    contactId: '',
    origin: 'manual',
    issueOn: currentAssociationDate ?? '',
    dueOn: currentAssociationDate ?? '',
    sendEmail: true,
    lineItems: [{ description: '', amount: '' }],
  })
  const [contactSelectionError, setContactSelectionError] = React.useState<string>()
  const lineItemError = apiFieldMessages(error, 'line_items')[0]
  const contactError = contactSelectionError ?? apiFieldMessages(error, 'contact')[0]

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (!form.contactId) {
              setContactSelectionError('Select a Contact.')
              return
            }

            setContactSelectionError(undefined)
            onSubmit(form)
          }}
        >
          <DialogHeader>
            <DialogTitle>New manual Invoice</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <ContactSelectField
              contacts={contacts}
              contactsLoading={contactsLoading}
              contactError={contactError}
              form={form}
              setContactSelectionError={setContactSelectionError}
              setForm={setForm}
            />
            <FormField label="Category" error={apiFieldMessages(error, 'origin')[0]}>
              <InvoiceCategorySelect
                value={form.origin}
                onChange={(origin) => setForm({ ...form, origin })}
              />
            </FormField>
            <InvoiceDateFields form={form} setForm={setForm} />
            <LineItemsEditor
              error={lineItemError}
              form={form}
              setForm={setForm}
              taxRates={taxRates}
              taxRatesLoading={taxRatesLoading}
            />
            <label
              htmlFor="manual-invoice-send-email"
              className="flex items-center gap-2 text-body-sm text-foreground"
            >
              <Checkbox
                id="manual-invoice-send-email"
                checked={form.sendEmail}
                onCheckedChange={(checked) => setForm({ ...form, sendEmail: checked === true })}
              />
              Send Invoice email
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Creating...' : 'Create Invoice'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ContactSelectField({
  contacts,
  contactsLoading,
  contactError,
  form,
  setContactSelectionError,
  setForm,
}: {
  contacts: Contact[]
  contactsLoading: boolean
  contactError?: string
  form: InvoiceForm
  setContactSelectionError: React.Dispatch<React.SetStateAction<string | undefined>>
  setForm: React.Dispatch<React.SetStateAction<InvoiceForm>>
}) {
  return (
    <FormField label="Contact" error={contactError}>
      <Select
        value={form.contactId}
        onValueChange={(contactId) => {
          setContactSelectionError(undefined)
          setForm({ ...form, contactId })
        }}
        disabled={contactsLoading}
      >
        <SelectTrigger aria-label="Contact">
          <SelectValue placeholder={contactsLoading ? 'Loading Contacts' : 'Select Contact'} />
        </SelectTrigger>
        <SelectContent>
          {contacts.map((contact) => (
            <SelectItem key={contact.id} value={String(contact.id)}>
              {contact.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}

function InvoiceDateFields({
  form,
  setForm,
}: {
  form: InvoiceForm
  setForm: React.Dispatch<React.SetStateAction<InvoiceForm>>
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField label="Issue date" htmlFor="invoice-issue-on">
        <Input
          id="invoice-issue-on"
          type="date"
          value={form.issueOn}
          onChange={(event) => setForm({ ...form, issueOn: event.target.value })}
        />
      </FormField>
      <FormField label="Due date" htmlFor="invoice-due-on">
        <Input
          id="invoice-due-on"
          type="date"
          value={form.dueOn}
          onChange={(event) => setForm({ ...form, dueOn: event.target.value })}
        />
      </FormField>
    </div>
  )
}

function LineItemsEditor({
  error,
  form,
  setForm,
  taxRates,
  taxRatesLoading,
}: {
  error?: string
  form: InvoiceForm
  setForm: React.Dispatch<React.SetStateAction<InvoiceForm>>
  taxRates: TaxRate[]
  taxRatesLoading: boolean
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-body font-semibold text-foreground">Line items</p>
        <Button
          type="button"
          variant="outline"
          className="h-auto border-dashed px-1.5 py-0.5 text-micro text-muted-foreground"
          onClick={() =>
            setForm({
              ...form,
              lineItems: [...form.lineItems, { description: '', amount: '' }],
            })
          }
        >
          + Add line
        </Button>
      </div>
      {error && <p className="text-caption text-destructive">{error}</p>}
      <div className="space-y-2">
        {form.lineItems.map((lineItem, index) => (
          <LineItemRow
            key={index}
            index={index}
            lineItem={lineItem}
            form={form}
            setForm={setForm}
            taxRates={taxRates}
            taxRatesLoading={taxRatesLoading}
          />
        ))}
      </div>
    </div>
  )
}

function LineItemRow({
  index,
  lineItem,
  form,
  setForm,
  taxRates,
  taxRatesLoading,
}: {
  index: number
  lineItem: InvoiceFormLineItem
  form: InvoiceForm
  setForm: React.Dispatch<React.SetStateAction<InvoiceForm>>
  taxRates: TaxRate[]
  taxRatesLoading: boolean
}) {
  const hasTaxRates = taxRates.length > 0
  const columns = hasTaxRates
    ? 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.25rem] sm:grid-cols-[minmax(0,1fr)_8rem_12rem_2.25rem]'
    : 'grid-cols-[minmax(0,1fr)_2.25rem] sm:grid-cols-[minmax(0,1fr)_8rem_2.25rem]'

  return (
    <div className={`grid ${columns} items-center gap-2`}>
      <Input
        className={hasTaxRates ? 'col-span-3 sm:col-span-1' : 'col-span-2 sm:col-span-1'}
        aria-label={`Line ${index + 1} description`}
        value={lineItem.description}
        placeholder="Description"
        onChange={(event) =>
          setForm({
            ...form,
            lineItems: updateLineItem(form.lineItems, index, {
              description: event.target.value,
            }),
          })
        }
      />
      {hasTaxRates && (
        <Select
          value={lineItem.taxRateId ?? 'none'}
          onValueChange={(taxRateId) =>
            setForm({
              ...form,
              lineItems: updateLineItem(form.lineItems, index, {
                taxRateId: taxRateId === 'none' ? undefined : taxRateId,
              }),
            })
          }
          disabled={taxRatesLoading}
        >
          <SelectTrigger aria-label={`Line ${index + 1} Tax Rate`}>
            <SelectValue placeholder="No Tax" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Tax</SelectItem>
            {taxRates.map((taxRate) => (
              <SelectItem key={taxRate.id} value={String(taxRate.id)}>
                {taxRate.name} ({taxRate.percentage}%)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Input
        aria-label={`Line ${index + 1} amount`}
        inputMode="decimal"
        value={lineItem.amount}
        placeholder="0.00"
        onChange={(event) =>
          setForm({
            ...form,
            lineItems: updateLineItem(form.lineItems, index, {
              amount: event.target.value,
            }),
          })
        }
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 w-9 px-0"
        aria-label={`Remove line ${index + 1}`}
        disabled={form.lineItems.length === 1}
        onClick={() =>
          setForm({
            ...form,
            lineItems: form.lineItems.filter((_item, itemIndex) => itemIndex !== index),
          })
        }
      >
        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
      </Button>
    </div>
  )
}

function updateLineItem(
  lineItems: InvoiceFormLineItem[],
  index: number,
  patch: Partial<InvoiceFormLineItem>,
) {
  return lineItems.map((lineItem, itemIndex) =>
    itemIndex === index ? { ...lineItem, ...patch } : lineItem,
  )
}

export function submitInvoiceForm(form: InvoiceForm) {
  return postJSON<InvoiceResponse>('/api/v1/invoices', {
    invoice: {
      contact_id: Number(form.contactId),
      origin: form.origin,
      issue_on: form.issueOn,
      due_on: form.dueOn,
      send_email: form.sendEmail,
      line_items: form.lineItems.map((lineItem) => ({
        description: lineItem.description,
        amount: lineItem.amount,
        ...(lineItem.taxRateId ? { tax_rate_id: Number(lineItem.taxRateId) } : {}),
      })),
    },
  })
}
